'use strict';

/**
 * Servicio para determinar el estado de completitud de campos del paciente
 * basado en la estructura de visitas y estados de las visitas
 * 
 * @param {Object} ShareCrfSDK - SDK para llamadas a la API
 * @param {Object} RecordService - Servicio de registros
 * @param {Object} LoadingService - Servicio para manejar el estado de carga de promesas
 * @param {Object} RecordDataService - Servicio de datos de registros
 * @param {Object} $q - Servicio de promesas de Angular
 * @param {Object} $log - Servicio de logging de Angular
 * @param {Object} $injector - Servicio de inyección de dependencias de Angular
 * @param {Object} $rootScope - Scope raíz de Angular para emitir eventos
 * @returns {Object} API pública del servicio
 */
angular.module('ecrd').factory('FieldCompletionStatusService', [
    'ShareCrfSDK',
    'RecordService',
    'LoadingService',
    'RecordDataService',
    '$q',
    '$log',
    '$injector',
    function FieldCompletionStatusService(
        ShareCrfSDK,
        RecordService,
        LoadingService,
        RecordDataService,
        $q,
        $log,
        $injector
    ) {
        var loadPromise = null;
        var visitsStates = null;
        var VisitsConfig = null;

        // Try to get VisitsConfig if it exists
        try {
            if ($injector.has('VisitsConfig')) {
                VisitsConfig = $injector.get('VisitsConfig');
            }
        }
        catch (error) {
            // VisitsConfig is not available, continue without it
            $log.debug('FieldCompletionStatusService: VisitsConfig not available');
        }

        function _wrapRequest(request) {
            return LoadingService.addPromise(request);
        }

        function listRecordVisits(params) {
            return _wrapRequest(ShareCrfSDK.ecrd.listRecordVisits(params));
        }

        /**
         * Carga los estados de visitas del paciente
         * @returns {Promise<Array>} Promesa que resuelve con los estados de visitas
         */
        function loadVisitsStates() {
            var recordId = RecordService.getId();

            if (!recordId) {
                return $q.reject(new Error('No record loaded'));
            }

            if (loadPromise) {
                return loadPromise;
            } 

            loadPromise = listRecordVisits({ recordId: recordId })
                .then(function(response) {
                    var rows = response.rows || response.data || [];
                    var rowsByUid = {};
                    rows.forEach(function(row) {
                        rowsByUid[row.element_uid] = row;
                    });
                    visitsStates = rowsByUid;

                    return visitsStates;
                })
                .finally(function() {
                    loadPromise = null;
                });
            
            return loadPromise;
        }

        /**
         * Limpia el estado de visitas del paciente
         */
        function clearVisitsStates() {
            visitsStates = null;
            loadPromise = null;
        }

        /**
         * Determina el estado de completitud de un campo específico
         * 
         * @param {string} uid UID del campo
         * @return {string} Estado de completitud: 'completed', 'overdue', 'planned', 'pending'
         */
        function getFieldCompletionStatus(uid) {
            var recordId = RecordService.getId();
            
            if (!recordId) {
                return 'pending';
            }

            var isFieldEmpty = RecordDataService.isFieldValueEmpty(uid);
            if (!isFieldEmpty) {
                return 'completed';
            }

            if (!VisitsConfig || !VisitsConfig.visitFieldMappings) {
                return 'pending';
            }

            // Buscar la visita asociada al campo usando el mapeo
            var fieldVisitId = null;
            var visitFieldMappings = VisitsConfig.visitFieldMappings;
            
            for (var visitId in visitFieldMappings) {
                var fieldIds = visitFieldMappings[visitId];
                if (fieldIds && fieldIds.indexOf(uid) !== -1) {
                    fieldVisitId = visitId;
                    break;
                }
            }

            if (!fieldVisitId || !visitsStates) {
                return 'pending';
            }

            // Obtener el estado de la visita
            var visitState = visitsStates[fieldVisitId];
            if (!visitState) {
                return 'pending';
            }

            // Determinar el estado basado en el estado de la visita
            var finalStatus;
            switch (visitState.status) {
                case 'closed':
                    finalStatus = 'overdue';
                    break;
                case 'pending':
                    finalStatus = 'planned';
                    break;
                case 'active':
                default:
                    finalStatus = 'pending';
                    break;
            }

            return finalStatus;
        }

        return {
            getFieldCompletionStatus: getFieldCompletionStatus,
            loadVisitsStates: loadVisitsStates,
            clearVisitsStates: clearVisitsStates,
        };
    },
]); 
